<template>
  <v-text-field
    hide-details="auto"
    v-model="displayAmount"
    prefix="$"
    type="number"
    :min="allowNegative ? undefined : 0"
    step="0.01"
    :rules="rules"
    locale="en-US"
    :label="label || 'Amount'"
    :clearable="clearable"
    validate-on="eager"
    @update:model-value="emitCents"
  ></v-text-field>
</template>

<script setup lang="ts">
import { useValidation } from "@/composables/useValidation";
import { ref, watch } from "vue";

const props = defineProps<{
  amountCents?: number | null;
  allowNegative?: boolean | null;
  required?: boolean;
  label?: string;
  clearable?: boolean;
}>();

const validateion = useValidation();

const rules = computed(() =>
  ([validateAmount] as ((value: string | number) => string | true)[]).concat(
    props.required ? [validateion.required] : [],
  ),
);

const emit = defineEmits<{
  (e: "update:amountCents", value?: number): void;
}>();

const amountCents = ref(props.amountCents);

// Convert incoming cents → display as dollars
const displayAmount = ref<number | null>(
  props.amountCents == null ? null : props.amountCents / 100,
);

// Watch prop updates
watch(
  () => props.amountCents,
  (newVal) => {
    if (
      !displayAmount.value ||
      Math.round(displayAmount.value * 100) !== newVal
    ) {
      displayAmount.value = newVal == null ? null : newVal / 100;
    }
  },
);

// Emit updated cents value when the field changes
function emitCents() {
  if (displayAmount.value === null || displayAmount.value === undefined) {
    amountCents.value = null;
    emit("update:amountCents", undefined);
    return;
  }
  const cents = Math.round(Number(displayAmount.value) * 100);
  if (amountCents.value != cents) {
    amountCents.value = cents;
    emit("update:amountCents", cents);
  }
}

// Optional: simple validation
function validateAmount(value: number | string) {
  const num = Number(value);
  if (isNaN(num) || (num < 0 && !props.allowNegative))
    return "Amount must be a positive number";
  return true;
}
</script>
